"use client";
import React from "react";
import { motion } from "framer-motion";
import { FaExclamationTriangle, FaWallet } from "react-icons/fa";
import CountUp from "react-countup";
import { useRouter } from "next/navigation";
import { FaCircleXmark, FaSquareArrowUpRight } from "react-icons/fa6";
import PropTypes from "prop-types";
import clsx from "clsx";

const TarjetaEstadoCuenta = ({ user, deudas }) => {
  const router = useRouter();
  const { total, error, isLoading, statusInfo } = deudas;
  const tieneDeuda = Number(total) > 0;
  const isPending = statusInfo;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
      className="group relative rounded-2xl bg-blue-100 p-4 transition-shadow duration-300 sm:p-6"
    >
      {isLoading && (
        <div className="flex h-full flex-col items-center justify-center">
          <div className="loading" /> Cargando informacion ...
        </div>
      )}
      {!isLoading && error && (
        <div className="flex h-full flex-col items-center justify-center px-16">
          <div className="justify-center rounded-full bg-red-100 p-4 text-red-600">
            <FaCircleXmark size={24} />
          </div>
          <div className="text-center font-bold text-red-500">{error}</div>
        </div>
      )}
      {/* ---------- SIN USUARIO ---------- */}
      {!user && !isLoading && !error && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold tracking-tight text-gray-600 sm:text-xl">
            Estado de cuenta
          </h3>

          <p className="text-sm text-gray-600">
            Conoce el estado de tus deudas
          </p>

          <button
            type="button"
            onClick={() => router.push("/consultas/deudas")}
            className="flex w-full place-content-center items-center gap-4 rounded-xl bg-primary px-6 py-3 font-medium text-white shadow-md transition hover:scale-[1.02]"
          >
            <FaSquareArrowUpRight />
            Haz click aqui
          </button>
        </div>
      )}

      {/* ---------- CON USUARIO ---------- */}
      {user && !isLoading && !error && (
        <>
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-700">
              Estado de cuenta
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* SALDO */}
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 transition hover:shadow-md">
              <div className="mb-2 flex items-center gap-3">
                <div className="rounded-lg bg-green-100 p-2 text-green-600">
                  <FaWallet size={16} />
                </div>
                <span className="font-medium text-gray-700">
                  Saldo pendiente
                </span>
              </div>

              <p className="text-xl font-semibold text-gray-800">
                <CountUp
                  className="text-gary-800 text-3xl font-bold sm:text-4xl"
                  end={total}
                  decimals={2}
                  prefix="$"
                  separator=","
                  formattingFn={(value) => {
                    const parts = value
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      .split(".");

                    const entero = parts[0];
                    const decimal = parts[1];

                    return `$${entero}.<span class="text-sm align-top">${decimal}</span>`;
                  }}
                />
              </p>

              <p className="mt-1 text-sm text-gray-500">
                {tieneDeuda
                  ? "Tiene valores pendientes por pagar"
                  : "No registra deudas pendientes"}
              </p>

              <div className="mt-3 flex items-center justify-between">
                <span
                  className={clsx(
                    "rounded-full px-2 py-1 text-xs font-medium",
                    tieneDeuda
                      ? "bg-red-100 text-red-600"
                      : "bg-green-100 text-green-600",
                  )}
                >
                  {tieneDeuda ? "Pendiente" : "Al día"}
                </span>
                {!tieneDeuda && !isPending && (
                  <button className="text-sm text-green-600 hover:underline">
                    Generar solvencia →
                  </button>
                )}
                {tieneDeuda && (
                  <button
                    type="button"
                    onClick={() => router.push("/consultas/deudas")}
                    className="text-sm text-green-600 hover:underline"
                  >
                    Ver detalles →
                  </button>
                )}
              </div>
            </div>

            {/* OBLIGACIONES */}
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 transition hover:shadow-md">
              <div className="mb-2 flex items-center gap-3">
                <div className="rounded-lg bg-yellow-100 p-2 text-yellow-600">
                  <FaExclamationTriangle size={16} />
                </div>
                <span className="font-medium text-gray-700">Obligaciones</span>
              </div>

              <p
                className={clsx(
                  "text-sm font-medium",
                  isPending ? "text-yellow-600" : "text-green-600",
                )}
              >
                {isPending ? "Pendientes" : "Sin obligaciones"}
              </p>

              <p className="mt-1 text-sm text-gray-500">
                {isPending
                  ? "Tiene obligaciones que impiden generar solvencia"
                  : "No tiene obligaciones pendientes"}
              </p>

              <div className="mt-3">
                <span
                  className={clsx(
                    "rounded-full px-2 py-1 text-xs font-medium",
                    isPending
                      ? "bg-orange-100 text-yellow-600"
                      : "bg-green-100 text-green-600",
                  )}
                >
                  {isPending ? "Pendientes" : "Al día"}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default TarjetaEstadoCuenta;

TarjetaEstadoCuenta.propTypes = {
  user: PropTypes.object.isRequired,
};
